import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useMemo } from "react";

import { isValidGroupedChartQuery } from "../../utils/DashboardUtils";
import useGroupedChartData from "./WidgetHooks/useGroupedChartData";
import GroupedChartTooltip from "./Tooltips/GroupedChartTooltip";
import { IsGroupSelected } from "../../utils/ChartUtils";
import { CustomLegendLabel } from "./PieChartWidget";
import { BaseChartProps } from "./WidgetTypes";
import useThemeColors from "./useThemeColors";
import WidgetLoading from "./WidgetLoading";
import WidgetError from "./WidgetError";

// OLD CHART Demo - https://bizcharts.taobao.com/product/BizCharts4/demo/322
// New chart can be found in recharts examples.
const GroupedBarChartWidget = ({
  section,
  setSettings,
  record,
  theme,
  topLevelFilters,
  viewFilters,
  onSelectGroup,
  selectedGroup,
}: BaseChartProps) => {
  const {
    data,
    keys,
    references,
    error,
    loading,
    xAxisField,
    xAxisFieldName,
    groupByField,
    groupByFieldType,
    groupByFieldName,
  } = useGroupedChartData({
    moduleAPIName: section?.ModuleAPIName,
    query: section?.Query,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    validator: isValidGroupedChartQuery,
    record: record,
    queryKeyID: section?.id,
  });
  const { colors } = useThemeColors({ theme, chartData: data as any });

  const MyChart = useMemo(() => {
    if (loading || !data) return null;

    const uniqueKeysArray = keys?.size ? [...keys] : [];

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          {/* <CartesianGrid strokeDasharray="3 3" /> */}
          <XAxis
            dataKey={xAxisField}
            fontSize={14}
            tickLine={false}
            fontWeight={500}
            tickFormatter={(value) => value || `No ${groupByFieldName}`}
          />
          <YAxis hide fontSize={14} />
          <Tooltip
            cursor={{ fill: "var(--superlight)" }}
            content={(props) => (
              <GroupedChartTooltip
                {...props}
                xAxisField={xAxisField}
                xAxisFieldName={xAxisFieldName}
                groupByFieldType={groupByFieldType}
              />
            )}
          />
          {uniqueKeysArray.map((key: string, i: number) => {
            const isSelected = IsGroupSelected({
              groupByField,
              selectedGroup,
              groupValue: { [groupByField]: references?.get(key) ?? key },
              rawGroupByField: section?.Query?.GroupBy?.[0]?.Field,
              groupByFieldType,
            });

            const finalFill =
              selectedGroup !== undefined ? `${colors[i]}80` : colors[i];

            return (
              <Bar
                key={key}
                name={key}
                dataKey={`${key}.val`}
                stackId={section?.ChartStyle === "Stacked" ? "a" : i}
                fill={isSelected ? colors[i] : finalFill}
                stroke={isSelected ? `${colors[i]}80` : undefined}
                strokeWidth={4}
                // style={{ cursor: "pointer" }}
                // onClick={(entry) => {
                //   onSelectGroup &&
                //     onSelectGroup([
                //       {
                //         Group: true,
                //         Groups: [
                //           {
                //             groupByField: xAxisField,
                //             groupByValue: entry[xAxisField],
                //           },
                //           {
                //             groupByField: groupByField,
                //             groupByValue: entry.tooltipPayload[0].name,
                //           },
                //         ],
                //       },
                //     ]);
                // }}
              />
            );
          })}
          <Legend
            formatter={(item) => {
              let value = item;
              const isGroupReferenceField = [
                "MultiReference",
                "SingleReference",
              ].includes(groupByFieldType);

              if (isGroupReferenceField) {
                const result = (data as any[])?.find((v) => !!v[item]);

                value = result ? result[item]?.OriginalValue?.id ?? item : item;
              }

              if (groupByFieldType === "Tags") {
                const match = data.find((d) => !!d[value]);
                item = match[value]?.Name ?? item;
              }

              return CustomLegendLabel({
                data: item,
                groupByField,
                groupByFieldName,
                rawGroupByField: section?.Query?.GroupBy[0]?.Field,
                value,
                onSelectGroup,
                filters: section?.Query?.Filters,
              });
            }}
            iconSize={12}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }, [
    data,
    keys,
    groupByField,
    loading,
    section?.ChartStyle,
    section?.Orientation,
    xAxisField,
    colors,
    selectedGroup,
  ]);

  if (error) {
    return (
      <WidgetError
        onOpenSettings={setSettings ? () => setSettings(section) : null}
      />
    );
  }

  if (loading) {
    return <WidgetLoading />;
  }

  return MyChart;
};

export default GroupedBarChartWidget;
